import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useSetHandsetLimits, type AdminHandsetLimits } from "@/features/admin";
import { useEffect, useState } from "react";
import { toast } from "sonner";

/**
 * The three figures platform staff set on a handset.
 *
 * Deliberately not the whole handset: renaming a client's phone or moving its
 * caller ID from here would be reaching into their configuration, and this
 * dialog exists for the part that is ours. The tenant's own choices are shown
 * beside each field, read-only, because the pair is what makes either
 * meaningful — a ceiling of "+33, +32" under a tenant list of "+33" is a client
 * who does not want Belgium, not a misconfiguration.
 */
export const HandsetLimitsDialog = ({
  handset,
  onClose,
}: {
  handset: AdminHandsetLimits | null;
  onClose: () => void;
}) => {
  const setLimits = useSetHandsetLimits();
  const [outbound, setOutbound] = useState(false);
  const [prefixes, setPrefixes] = useState("");
  const [maxSeconds, setMaxSeconds] = useState("");

  useEffect(() => {
    if (!handset) return;
    setOutbound(handset.outbound_enabled);
    setPrefixes((handset.max_prefixes || []).join(", "));
    setMaxSeconds(handset.max_call_seconds ? String(handset.max_call_seconds) : "");
  }, [handset]);

  const save = async () => {
    if (!handset) return;
    try {
      await setLimits.mutateAsync({
        clientId: handset.client_id,
        deviceId: handset.id,
        outbound_enabled: outbound,
        max_prefixes: prefixes
          .split(",")
          .map((prefix) => prefix.trim())
          .filter(Boolean),
        max_call_seconds: Number(maxSeconds) || 0,
      });
      toast.success("Limites enregistrées");
      onClose();
    } catch (error) {
      // The backend refuses a malformed prefix — a bare "+" above all — with a
      // message written to be read by a human. Show it rather than a generic one.
      toast.error(
        error instanceof Error ? error.message : "Enregistrement impossible",
      );
    }
  };

  return (
    <Dialog open={Boolean(handset)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {handset?.label} — {handset?.client_name}
          </DialogTitle>
          <DialogDescription>
            Ce que ce poste peut nous coûter. Le client règle le reste lui-même.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div className="space-y-0.5">
              <Label>Appels sortants autorisés</Label>
              <p className="text-muted-foreground text-xs">
                Coupé, ce poste ne peut appeler que HeyUp. C'est l'état par
                défaut d'un poste qui vient d'être créé.
              </p>
            </div>
            <Switch checked={outbound} onCheckedChange={setOutbound} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-max-prefixes">Préfixes autorisés</Label>
            <Input
              id="admin-max-prefixes"
              value={prefixes}
              placeholder="+33, +32"
              onChange={(e) => setPrefixes(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              Vide = le pays du numéro présenté ({handset?.caller_id || "—"}).
              Un « + » seul est refusé : tout numéro E.164 commence par là.
            </p>
            <p className="text-muted-foreground text-xs">
              Choix du client, dans cette limite :{" "}
              <span className="font-mono">
                {(handset?.allowed_prefixes || []).join(", ") || "tous"}
              </span>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-max-call">Durée maximale d'un appel (s)</Label>
            <Input
              id="admin-max-call"
              type="number"
              min={0}
              value={maxSeconds}
              placeholder="3600"
              onChange={(e) => setMaxSeconds(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              Le garde-fou contre l'appel qui ne se termine jamais, appliqué par
              Asterisk lui-même. Vide = 1 h. Le quota mensuel, lui, est au client :{" "}
              {handset?.monthly_minutes_cap
                ? `${handset.monthly_minutes_cap} min`
                : "illimité"}
              .
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={save} disabled={setLimits.isPending}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
